/**
 * theme.ts — canonical design tokens for the Slot Tournament app.
 *
 * Inspired by a vintage slot-machine cabinet:
 *   - Topper: purple harlequin pattern, gold "JACKPOTS" wordmark, flaming 7s
 *   - Belly glass: royal blue field, red/gold typography, chrome bezels
 *   - Reels: black backdrop, red 7-segment LED for credits
 *
 * Tailwind reads these tokens via tailwind.config.ts. Keep that file in sync
 * if you add a token here.
 */

export const palette = {
  purple: {
    base: '#3d1a6e',
    deep: '#2a0f50',
    ink: '#1a0936',
  },
  blue: {
    base: '#1e3a8a',
    light: '#2b54c9',
  },
  gold: {
    base: '#fbbf24',
    bright: '#fcd34d',
    deep: '#d97706',
  },
  flame: {
    orange: '#f97316',
    red: '#dc2626',
    ember: '#991b1b',
  },
  cream: '#fef3c7',
  chrome: {
    base: '#c0c4cc',
    light: '#e6e9ee',
    dark: '#6b7280',
  },
  led: {
    on: '#ff2a1f',
    dim: '#3a0a07',
  },
} as const;

export const gradients = {
  gold: 'linear-gradient(180deg, #fde68a 0%, #fbbf24 45%, #d97706 100%)',
  flame: 'linear-gradient(180deg, #fcd34d 0%, #f97316 40%, #dc2626 100%)',
  purpleDeep: 'linear-gradient(180deg, #3d1a6e 0%, #1a0936 100%)',
  chromeBevel: 'linear-gradient(180deg, #e6e9ee 0%, #c0c4cc 45%, #8b909a 100%)',
} as const;

export const typography = {
  display: {
    family: '"Anton", "Bebas Neue", Impact, system-ui, sans-serif',
    note: 'Tall condensed sans for marquee text. Falls back to Bebas/Impact.',
  },
  body: {
    family: '"Inter Variable", Inter, system-ui, sans-serif',
  },
  led: {
    family: '"DSEG7-Classic", "DSEG7Classic-Bold", "Share Tech Mono", monospace',
    note: 'Self-hosted at /fonts/dseg7/. Run scripts/fetch-fonts.sh after first checkout.',
  },
} as const;

export const motifs = {
  diamondPatternStops: '#3d1a6e 0 25%, #2a0f50 25% 50%',
  diamondPatternSize: '48px 48px',
} as const;

export const brand = {
  /** App-level wordmark. */
  appName: 'PARKS SLOTS',
  /** Default machine label shown on tournament cards. */
  defaultMachineName: 'Home Slots',
} as const;

export type Palette = typeof palette;
export type Gradients = typeof gradients;
